import { Link } from "react-router-dom";
import { HOME_PEOPLE, ASSETS } from "../mock";
import { useTexts } from "../hooks/useTexts";
import SectionTitle from "../components/SectionTitle";
import PageSeo from "../components/PageSeo";
import Reveal from "../components/Reveal";
import DonationWidget from "../components/DonationWidget";
import HtmlOrText from "../components/HtmlOrText";
import { ArrowRight, Heart, BookOpen, Users, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";

export default function Home() {
  const t = useTexts();
  return (
    <div>
      <PageSeo
        title="Inicio"
        description="Asociación Pequeño Fresno: informamos sobre el Complejo de Esclerosis Tuberosa (CET) y apoyamos a las familias afectadas. Únete a nuestra causa en Alicante."
        path="/"
        image={ASSETS.lia}
      />

      {/* Cinematic Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 pt-8 lg:pt-14 pb-10 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6 relative z-10">
            <Reveal>
              <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-semibold" style={{ background: "var(--bg-mid)", color: "var(--brand)" }}>
                <Sparkles className="w-4 h-4" /> {t.hero_badge}
              </span>
            </Reveal>
            <Reveal delay={100}>
              <h2 className="h-serif mt-5 text-5xl sm:text-6xl lg:text-7xl leading-[1.05] font-semibold text-[color:var(--brand-strong)]">
                {t.hero_title_1}
                <span className="block font-cursive text-[color:var(--accent)] mt-2 text-6xl sm:text-7xl lg:text-8xl font-normal">{t.hero_title_2}</span>
              </h2>
            </Reveal>
            <Reveal delay={200}>
              <p className="mt-6 text-lg leading-relaxed text-[color:var(--text)] max-w-xl">
                {t.hero_subtitle}
              </p>
            </Reveal>
            <Reveal delay={300}>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/cet" className="btn-brand inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold shadow-lg">
                  <BookOpen className="w-4 h-4" /> Conoce el CET
                </Link>
                <a href="https://donorbox.org/ayudanos-934402" target="_blank" rel="noreferrer" className="btn-accent inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold shadow-lg">
                  <Heart className="w-4 h-4" /> Ayúdanos
                </a>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-6 relative">
            <Reveal delay={200}>
              <div className="relative aspect-[4/5] max-w-md mx-auto">
                {/* Background blob */}
                <div className="absolute -inset-6 rounded-[48%_52%_60%_40%/45%_55%_45%_55%] pf-gradient opacity-90 animate-floaty-slow" />
                <div className="absolute inset-0 rounded-[42%_58%_55%_45%/50%_50%_50%_50%] overflow-hidden shadow-2xl ring-8 ring-white">
                  <img src={ASSETS.lia} alt="Lia" className="w-full h-full object-cover" onError={(e) => (e.currentTarget.src = ASSETS.family)} />
                </div>
                <img src={ASSETS.butterflyLeaf} alt="" className="absolute -bottom-6 -left-10 w-40 animate-floaty" />
                <img src={ASSETS.butterflyLeaf} alt="" className="absolute -top-8 -right-6 w-28 rotate-45 opacity-80 animate-floaty-slow" />
              </div>
            </Reveal>
          </div>
        </div>

        {/* soft decorative wave */}
        <div className="h-16 -mb-1" style={{ background: "linear-gradient(to bottom, transparent, var(--bg))" }} />
      </section>

      {/* Quienes somos */}
      <section className="max-w-6xl mx-auto px-6 pt-8">
        <SectionTitle>~ quienes somos ~</SectionTitle>

        <div className="grid md:grid-cols-3 gap-10 mt-4">
          {HOME_PEOPLE.map((p, idx) => (
            <Reveal key={p.id} delay={idx * 120}>
              <div className="text-center">
                <div className="relative mx-auto w-64 h-64 sm:w-72 sm:h-72">
                  <div className="absolute inset-0 rounded-full overflow-hidden shadow-xl hover-lift ring-8 ring-white">
                    <img
                      src={p.img}
                      alt={p.name}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.currentTarget.src = ASSETS.leafDeco; }}
                    />
                  </div>
                  <img src={ASSETS.butterflyLeaf} alt="" className="absolute -bottom-4 -left-6 w-20 rotate-[-15deg]" />
                </div>
                <h3 className="font-cursive text-5xl mt-6 text-[color:var(--brand)]">{p.name}</h3>
                <p className="mt-1 text-[color:var(--text-soft)] italic">{p.subtitle}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Intro paragraph */}
      <section className="max-w-3xl mx-auto px-6 mt-20 text-center">
        <Reveal>
          <div className="text-lg text-[color:var(--text)] text-left sm:text-center">
            <HtmlOrText html={t.home_intro} />
          </div>
        </Reveal>
      </section>

      {/* CTA cards */}
      <section className="max-w-6xl mx-auto px-6 mt-16 grid md:grid-cols-2 gap-8">
        <Reveal>
          <Link to="/cet" className="block relative pf-soft rounded-3xl p-10 hover-lift group overflow-hidden">
            <img src={ASSETS.smallLeaf} alt="" className="absolute -right-6 -top-6 w-32 opacity-70" />
            <h3 className="h-serif text-3xl sm:text-4xl font-semibold text-[color:var(--brand-strong)]">{t.home_cta_cet_title}</h3>
            <div className="mt-3 text-[color:var(--text)]"><HtmlOrText html={t.home_cta_cet_body} /></div>
            <div className="mt-6 inline-flex items-center gap-2 text-[color:var(--brand)] font-semibold group-hover:gap-4 transition-all">
              Leer más <ArrowRight className="w-5 h-5" />
            </div>
          </Link>
        </Reveal>

        <Reveal delay={150}>
          <Link to="/asociacion" className="block relative pf-soft rounded-3xl p-10 hover-lift group overflow-hidden">
            <img src={ASSETS.smallLeaf} alt="" className="absolute -right-6 -top-6 w-32 opacity-70 -scale-x-100" />
            <h3 className="h-serif text-3xl sm:text-4xl font-semibold text-[color:var(--brand-strong)]">{t.home_cta_asoc_title}</h3>
            <div className="mt-3 text-[color:var(--text)]"><HtmlOrText html={t.home_cta_asoc_body} /></div>
            <div className="mt-6 inline-flex items-center gap-2 text-[color:var(--brand)] font-semibold group-hover:gap-4 transition-all">
              Únete <ArrowRight className="w-5 h-5" />
            </div>
          </Link>
        </Reveal>
      </section>

      {/* Donate Banner */}
      <section className="max-w-5xl mx-auto px-6 mt-20">
        <Reveal>
          <div className="pf-gradient rounded-3xl p-10 sm:p-14 text-center relative overflow-hidden">
            <img src={ASSETS.butterflyLeaf} alt="" className="absolute -left-8 -bottom-8 w-40 opacity-60 rotate-12" />
            <img src={ASSETS.butterflyLeaf} alt="" className="absolute -right-8 -top-8 w-40 opacity-60 -rotate-12" />
            <h3 className="h-serif text-4xl sm:text-5xl font-semibold text-[color:var(--brand-strong)] relative">{t.home_banner_title}</h3>
            <div className="mt-4 max-w-2xl mx-auto text-[color:var(--text)] relative"><HtmlOrText html={t.home_banner_body} /></div>
            <div className="mt-8 relative">
              <DonationWidget />
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}

function ImpactStats() {
  const stats = [
    { end: 40, label: "Familias apoyadas", suffix: "+" },
    { end: 12, label: "Eventos solidarios", suffix: "" },
    { end: 6, label: "Colaboradores", suffix: "+" },
    { end: 2024, label: "Fundada en", suffix: "" },
  ];
  return (
    <section className="max-w-6xl mx-auto px-6 mt-4">
      <Reveal>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pf-soft rounded-3xl p-6 sm:p-8">
          {stats.map((s, i) => (
            <div key={i} className="text-center">
              <div className="h-serif text-4xl sm:text-5xl font-semibold text-[color:var(--brand-strong)]">
                <Counter target={s.end} />{s.suffix}
              </div>
              <div className="text-sm text-[color:var(--text-soft)] mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </Reveal>
    </section>
  );
}

function Counter({ target }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    let raf;
    const start = performance.now();
    const dur = 1600;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / dur);
      const ease = 1 - Math.pow(1 - p, 3);
      setN(Math.floor(ease * target));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target]);
  return <>{n.toLocaleString("es-ES")}</>;
}
